import * as THREE from "three";
import { loadPly, buildSplats } from "./ply";
import { createSplatMesh } from "./splatMesh";
import { createBackdrop, createVeil, coverFit } from "./backdrop";
import { createClouds } from "./clouds";
import { createEarth, createStars } from "./earth";
import { createConstellation } from "./constellation";

const FOV = 35;
const SUN_W = new THREE.Vector3(0.78, 0.07, -1).normalize();

// Scroll timeline over the full journey (0..1):
// shift   — satellite glides from composition 1 to composition 2
// clouds  — billboard cloud deck fades in under the satellite
// veil    — fullscreen cloud cover peaks and clears (space fully hidden)
// backdrop— sunset plate crossfades in behind the veil
// acft    — aircraft enters from its heading offset and settles center
const PHASE = {
  shift: [0.06, 0.50],
  clouds: [0.28, 0.56],
  veil: [0.40, 0.55, 0.57, 0.70],
  backdrop: [0.50, 0.63],
  acft: [0.62, 0.80],
};

const ss = (a, b, x) => { const t = Math.min(Math.max((x - a) / (b - a), 0), 1); return t * t * (3 - 2 * t); };

function readPose() {
  const q = new URLSearchParams(window.location.search).get("pose");
  return q ? q.split(",").map(Number) : null;
}

export class FlightScene {
  constructor(canvas, { getScroll, getPointer, reduced, onReady, onError, onProgress }) {
    this.canvas = canvas;
    this.getScroll = getScroll;
    this.getPointer = getPointer;
    this.reduced = reduced;
    this.onReady = onReady;
    this.onError = onError;
    this.onProgress = onProgress;
    this.pose = readPose() || [0.62, 0.42, -0.5, 0];
    this.state = { t: 0, sp: 0, px: 0, py: 0, phase: 0, fade: 0, alpha: 0 };
    this.travel = new THREE.Vector3();
    this.running = false;
    this.visible = true;
    this.frozen = false;
    this.first = true;

    const r = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: "high-performance" });
    r.setClearColor(0x030509, 1);
    r.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer = r;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 0.05, 900);
    this.camera.rotation.order = "YXZ";
    this.rig = new THREE.Group();
    this.rig.rotation.order = "YXZ";
    this.scene.add(this.rig);
    this.tmp = { v: new THREE.Vector3(), m: new THREE.Matrix4(), q: new THREE.Quaternion() };
    window.__flightScene = this;
  }

  async init() {
    const step = (f) => this.onProgress?.(f);
    const texP = new THREE.TextureLoader().loadAsync("/hero/plate.jpg").then((t) => { step(0.25); return t; });
    const satP = loadPly("/hero/satellite.ply").then((p) => { step(0.55); return p; });
    const droneP = loadPly("/hero/drone.ply").then((p) => { step(0.92); return p; });
    const [tex, satPly, dronePly] = await Promise.all([texP, satP, droneP]);
    step(1);

    tex.wrapS = tex.wrapT = THREE.MirroredRepeatWrapping;
    tex.anisotropy = 4;
    this.backdrop = createBackdrop(tex);
    this.veil = createVeil();
    this.clouds = createClouds(190);
    this.earth = createEarth();
    this.stars = createStars();

    // ---- satellite (composition 1 -> composition 2, driven by scroll)
    const sat = buildSplats(satPly);
    this.satSplat = createSplatMesh(sat);
    this.satSplat.mesh.rotation.set(-Math.PI / 2, 0, 0);
    this.satSplat.mesh.renderOrder = 2;
    this.satGroup = new THREE.Group();
    this.satGroup.add(this.satSplat.mesh);
    this.constellation = createConstellation();
    this.constellation.group.scale.setScalar(0.38);
    this.satGroup.add(this.constellation.group);
    this.satGroup.position.set(-1.15, 0.28, -5.0);
    this.satGroup.scale.setScalar(2.3);

    this.scene.add(
      this.stars.points,
      this.earth.group,
      this.satGroup,
      this.backdrop.mesh,
      this.veil.mesh,
      this.clouds.mesh
    );

    this.earth.group.position.set(11, -13.5, -44);

    this.resize();
    this.start();

    // ---- aircraft (existing scene, preserved)
    const splats = buildSplats(dronePly);
    this.report = splats.report;
    // eslint-disable-next-line no-console
    console.info("[hero] PLY model data:", splats.report, `${splats.count} splats rendered`);
    this.splat = createSplatMesh(splats);
    const flip = this.pose[3] ? 1 : -1;
    this.splat.mesh.rotation.set(flip * Math.PI / 2, 0, this.pose[3] ? Math.PI : 0);
    this.splat.mesh.renderOrder = 6;
    this.rig.add(this.splat.mesh);
    this.resize();
    this.onReady?.(splats);
  }

  resize() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const qdpr = parseFloat(new URLSearchParams(window.location.search).get("dpr") || "0");
    const dpr = qdpr > 0 ? qdpr : Math.min(window.devicePixelRatio || 1, w < 768 ? 1.5 : 1.35);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    const aspect = w / h;
    this.aspect = aspect;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.tanY = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.tanX = this.tanY * aspect;
    const fit = coverFit(aspect);
    this.fit = fit;
    this.pitchBase = -Math.atan((fit.horizon - 0.5) * 2 * this.tanY);
    const frac = aspect < 1 ? 0.9 : aspect < 1.4 ? 0.6 : 0.4;
    this.dist = 0.95 / (frac * 2 * this.tanX);
    this.yOff = aspect < 1 ? -0.02 : -0.1;
    this.satFit = aspect < 1 ? 0.68 : aspect < 1.4 ? 0.85 : 1;
    this.earthOff = aspect < 1 ? { x: 7, y: -3.5 } : { x: 0, y: 0 };
    const u = this.backdrop?.material.uniforms;
    if (u) {
      u.uRes.value.set(w * dpr, h * dpr);
      u.uCenter.value.set(...fit.center);
      u.uVis.value.set(...fit.vis);
      u.uHorizon.value = fit.horizon;
      u.uTan.value.set(this.tanX, this.tanY);
    }
    if (this.splat) {
      const su = this.splat.material.uniforms;
      su.uViewport.value.set(w * dpr, h * dpr);
      su.uFocal.value = (h * dpr) / (2 * this.tanY);
    }
    if (this.satSplat) {
      const su = this.satSplat.material.uniforms;
      su.uViewport.value.set(w * dpr, h * dpr);
      su.uFocal.value = (h * dpr) / (2 * this.tanY);
    }
    if (this.reduced) this.renderOnce();
  }

  setReduced(v) { this.reduced = v; this.renderOnce(); }
  setVisible(v) { this.visible = v; }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now) => {
      if (!this.running) return;
      this.raf = requestAnimationFrame(loop);
      const dt = Math.min((now - this.last) / 1000, 0.05);
      this.last = now;
      if (this.frozen) return;
      if (!this.visible || document.hidden) return;
      this.update(dt);
      this.renderer.render(this.scene, this.camera);
    };
    this.raf = requestAnimationFrame(loop);
  }

  renderOnce() {
    if (!this.backdrop) return;
    this.update(0);
    this.renderer.render(this.scene, this.camera);
  }

  update(dt) {
    const s = this.state;
    const m = this.reduced ? 0 : 1;
    const ease = (k) => 1 - Math.exp(-dt * k);
    const p = this.getScroll();
    const ptr = this.getPointer();
    const spTarget = ss(PHASE.acft[0], 1, p);
    if (this.first) { s.sp = spTarget; s.fade = 1; s.alpha = 1; this.first = false; }
    s.sp += (spTarget - s.sp) * (this.reduced ? 1 : ease(3.5));
    s.px += (ptr[0] * m - s.px) * ease(2);
    s.py += (ptr[1] * m - s.py) * ease(2);
    s.t += dt * m;
    s.fade = Math.min(1, s.fade + dt * 0.7 + (this.reduced ? 1 : 0));
    if (this.splat) s.alpha = Math.min(1, s.alpha + dt * 0.6 + (this.reduced ? 1 : 0));
    const t = s.t, sp = s.sp, rm = this.reduced ? 0.35 : 1;

    const satK = ss(PHASE.shift[0], PHASE.shift[1], p);
    const cloudIn = ss(PHASE.clouds[0], PHASE.clouds[1], p);
    const veil = ss(PHASE.veil[0], PHASE.veil[1], p) * (1 - ss(PHASE.veil[2], PHASE.veil[3], p));
    const backdropK = ss(PHASE.backdrop[0], PHASE.backdrop[1], p);
    const ent = ss(PHASE.acft[0], PHASE.acft[1], p);
    this.onProgress?.(p);

    // ---------- aircraft attitude: slow layered sines, no spin (existing behavior)
    const [Y0, P0, B0] = this.pose;
    const yaw = Y0 + m * (0.045 * Math.sin(t * 0.19) + 0.02 * Math.sin(t * 0.47 + 2)) + sp * 0.14 * rm + s.px * 0.05 + (1 - ent) * 0.2 * rm;
    const yawRate = m * (0.045 * 0.19 * Math.cos(t * 0.19) + 0.02 * 0.47 * Math.cos(t * 0.47 + 2));
    const bank = B0 + m * (0.05 * Math.sin(t * 0.31 + 1) + 0.018 * Math.sin(t * 0.83)) - yawRate * 3 - sp * 0.22 * rm - (1 - ent) * 0.14 * rm;
    const pitch = P0 + m * 0.03 * Math.sin(t * 0.27 + 0.6) - sp * 0.06 * rm + s.py * 0.03 + (1 - ent) * 0.08 * rm;
    this.rig.rotation.set(pitch, yaw, bank);

    const pb = this.pitchBase;
    const fwd = this.tmp.v.set(0, Math.sin(pb), -Math.cos(pb));
    const up = [0, Math.cos(pb), Math.sin(pb)];
    const d = this.dist;
    const yo = this.yOff * d * this.tanY + m * (0.022 * Math.sin(t * 0.53) + 0.009 * Math.sin(t * 1.31 + 1)) * d * 0.4;
    const xo = m * 0.012 * Math.sin(t * 0.23) * d;
    this.rig.position.set(fwd.x * d + xo, fwd.y * d + up[1] * yo, fwd.z * d + up[2] * yo);
    if (ent < 1) {
      // enter from screen-right (its direction of travel is leftward), settling to center
      const k = 1 - ent;
      this.rig.position.x += k * d * 1.35;
      this.rig.position.y -= k * 0.55;
    }

    // camera: scroll eases into a slightly lower, rolled angle (existing), space phase leans toward Earth
    const spaceCam = (1 - cloudIn) * rm;
    const cp = pb - sp * 0.035 * rm + s.py * 0.012 - spaceCam * 0.07;
    const cy = sp * 0.05 * rm - s.px * 0.02 - spaceCam * 0.06;
    const cr = -sp * 0.04 * rm + m * 0.006 * Math.sin(t * 0.37) + spaceCam * 0.02;
    this.camera.rotation.set(cp, cy, cr);
    this.camera.updateMatrixWorld();
    this.rig.updateMatrixWorld(true);

    // travel opposite to the aircraft's horizontal heading (ambient drift; storms past during descent)
    const nose2 = new THREE.Vector3(0, 0, 1).applyQuaternion(this.rig.quaternion);
    const vdir = new THREE.Vector3(-nose2.x, 0, -nose2.z).normalize();
    const speed = (2.4 + sp * 2.2) * m + veil * 4.5 * m;
    this.travel.addScaledVector(vdir, speed * dt);
    s.phase += dt * m * (0.3 + sp * 0.28);

    const vpw = this.camera.position.clone().addScaledVector(vdir, 1e4).project(this.camera);
    const sunNdc = this.camera.position.clone().addScaledVector(SUN_W, 1e4).project(this.camera);

    const bu = this.backdrop.material.uniforms;
    const e = this.camera.matrixWorld.elements;
    bu.uCamR.value.set(e[0], e[1], e[2]);
    bu.uCamU.value.set(e[4], e[5], e[6]);
    bu.uCamF.value.set(-e[8], -e[9], -e[10]);
    bu.uRoll.value = cr;
    bu.uShift.value.set(0.5 * Math.tan(cy) / this.tanX, -0.5 * (Math.tan(cp) - Math.tan(pb)) / this.tanY);
    bu.uVP.value.set(vpw.x * 0.5 + 0.5, vpw.y * 0.5 + 0.5);
    bu.uPhase.value = s.phase;
    bu.uFlowK.value = 0.05 + sp * 0.02;
    bu.uTravel.value.set(this.travel.x, this.travel.z);
    bu.uSunW.value.copy(SUN_W);
    bu.uTime.value = t;
    bu.uFade.value = s.fade * backdropK;
    const cu = this.clouds.material.uniforms;
    cu.uTravel.value.copy(this.travel);
    cu.uSunScreen.value.set(sunNdc.x, sunNdc.y);
    cu.uFade.value = s.fade * cloudIn;
    cu.uWarm.value = backdropK;
    cu.uSize.value = 1 + veil * 0.9;
    const vu = this.veil.material.uniforms;
    vu.uTime.value = t;
    vu.uK.value = veil;
    vu.uWarm.value = backdropK;

    // ---------- satellite: scroll-driven shift + ambient cruise + pointer glide
    const sf = this.satFit;
    const ax = (-0.35 + (1 - sf) * 0.5) * sf, ay = -0.42 + (1 - sf) * 0.3, az = -4.6, as = 2.15 * sf;
    const bx = 1.95 * sf, by = 0.6 + (1 - sf) * 0.45, bz = -6.6, bs = 1.45 * sf;
    const g = this.satGroup;
    g.position.set(
      THREE.MathUtils.lerp(ax, bx, satK) + m * 0.045 * Math.sin(t * 0.27) + s.px * 0.12,
      THREE.MathUtils.lerp(ay, by, satK) + m * 0.05 * Math.sin(t * 0.4 + 1) - s.py * 0.08,
      THREE.MathUtils.lerp(az, bz, satK)
    );
    g.scale.setScalar(THREE.MathUtils.lerp(as, bs, satK));
    g.rotation.x = m * 0.02 * Math.sin(t * 0.23) - s.py * 0.06;
    g.rotation.y = THREE.MathUtils.lerp(-0.15, 1.05, satK) + m * 0.03 * Math.sin(t * 0.19) + s.px * 0.09;
    g.rotation.z = THREE.MathUtils.lerp(0.06, 0.34, satK) + m * 0.015 * Math.sin(t * 0.31 + 2);

    // ---------- earth + stars ambience
    this.earth.update(dt);
    this.earth.group.position.x = 11 + (this.earthOff ? this.earthOff.x : 0);
    this.earth.group.position.y = -13.5 + (this.earthOff ? this.earthOff.y : 0) - satK * 9.5;
    this.earth.group.visible = backdropK < 0.999;
    const su = this.earth.uniforms;
    su.uGlobal.value = 1 - ss(0.55, 0.66, p);
    this.stars.uniforms.uTime.value = t;
    this.stars.uniforms.uGlobal.value = 1 - ss(0.5, 0.62, p);
    this.constellation.update(t);
    const fadeK = 1 - satK * 0.8;
    this.constellation.lineMat.opacity = 0.3 * fadeK * (0.86 + 0.14 * Math.sin(t * 1.1));
    this.constellation.spriteMat.opacity = 0.75 * fadeK;

    if (this.splat) {
      const dAlpha = ss(PHASE.acft[0] - 0.04, PHASE.acft[0] + 0.06, p);
      const su2 = this.splat.material.uniforms;
      su2.uAlpha.value = s.alpha * dAlpha;
      su2.uSunView.value.copy(SUN_W).transformDirection(this.camera.matrixWorldInverse);
      const mv = this.tmp.m.multiplyMatrices(this.camera.matrixWorldInverse, this.splat.mesh.matrixWorld);
      this.splat.sort(mv);
    }
    if (this.satSplat) {
      const su3 = this.satSplat.material.uniforms;
      su3.uSunView.value.copy(SUN_W).transformDirection(this.camera.matrixWorldInverse);
      const mv2 = this.tmp.m.multiplyMatrices(this.camera.matrixWorldInverse, this.satSplat.mesh.matrixWorld);
      this.satSplat.sort(mv2);
      su3.uAlpha.value = s.alpha;
    }
  }

  dispose() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.scene.traverse((o) => {
      o.geometry?.dispose();
      if (o.material) {
        Object.values(o.material.uniforms || {}).forEach((u) => u.value?.isTexture && u.value.dispose());
        o.material.dispose();
      }
    });
    this.renderer.dispose();
  }
}
